import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Plus, Edit3, Trash2, Loader2, GripVertical, ArrowUp, ArrowDown } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL || '';
const ENTITIES = ['contact', 'account', 'deal'];
const FIELD_TYPES = [
    { value: 'text', label: 'Text' },
    { value: 'textarea', label: 'Text Area' },
    { value: 'number', label: 'Number' },
    { value: 'date', label: 'Date' },
    { value: 'select', label: 'Dropdown' },
    { value: 'multi_select', label: 'Multi-Select' },
    { value: 'checkbox', label: 'Checkbox' },
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone' },
    { value: 'url', label: 'URL' },
];

export default function CustomFieldsTab() {
    const { getAuthHeader } = useAuth();
    const [entity, setEntity] = useState('contact');
    const [fields, setFields] = useState([]);
    const [loading, setLoading] = useState(true);

    // Dialog state
    const [showDialog, setShowDialog] = useState(false);
    const [editingField, setEditingField] = useState(null);
    const [form, setForm] = useState({ label: '', field_type: 'text', required: false, options: [], default_value: '' });
    const [optionInput, setOptionInput] = useState('');
    const [saving, setSaving] = useState(false);

    const loadFields = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/api/admin/custom-fields`, {
                headers: getAuthHeader(),
                params: { entity },
            });
            setFields(res.data || []);
        } catch { toast.error('Failed to load custom fields'); }
        finally { setLoading(false); }
    }, [entity, getAuthHeader]);

    useEffect(() => { loadFields(); }, [loadFields]);

    const openAdd = () => {
        setEditingField(null);
        setForm({ label: '', field_type: 'text', required: false, options: [], default_value: '' });
        setShowDialog(true);
    };

    const openEdit = (field) => {
        setEditingField(field);
        setForm({
            label: field.label,
            field_type: field.field_type,
            required: field.required,
            options: field.options || [],
            default_value: field.default_value || '',
        });
        setShowDialog(true);
    };

    const handleSave = async () => {
        if (!form.label.trim()) { toast.error('Label is required'); return; }
        setSaving(true);
        try {
            if (editingField) {
                await axios.put(`${API}/api/admin/custom-fields/${editingField.field_id}`, form, { headers: getAuthHeader() });
                toast.success('Field updated');
            } else {
                await axios.post(`${API}/api/admin/custom-fields`, { ...form, entity }, { headers: getAuthHeader() });
                toast.success('Field created');
            }
            setShowDialog(false);
            loadFields();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to save');
        }
        finally { setSaving(false); }
    };

    const handleDelete = async (fieldId) => {
        if (!window.confirm('Deactivate this field? Existing data will be preserved.')) return;
        try {
            await axios.delete(`${API}/api/admin/custom-fields/${fieldId}`, { headers: getAuthHeader() });
            toast.success('Field deactivated');
            loadFields();
        } catch { toast.error('Failed to delete'); }
    };

    const addOption = () => {
        if (optionInput.trim() && !form.options.includes(optionInput.trim())) {
            setForm(f => ({ ...f, options: [...f.options, optionInput.trim()] }));
            setOptionInput('');
        }
    };

    const removeOption = (opt) => {
        setForm(f => ({ ...f, options: f.options.filter(o => o !== opt) }));
    };

    const labelToKey = (label) => label.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');

    const showOptions = form.field_type === 'select' || form.field_type === 'multi_select';

    return (
        <div className="space-y-4">
            {/* Entity selector */}
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-1 bg-stone-100 rounded-lg p-1">
                    {ENTITIES.map(e => (
                        <button
                            key={e}
                            onClick={() => setEntity(e)}
                            className={`px-4 py-1.5 rounded-md text-sm font-medium capitalize transition-colors ${
                                entity === e ? 'bg-stone-900 text-white' : 'text-stone-600 hover:text-stone-900'
                            }`}
                        >
                            {e}
                        </button>
                    ))}
                </div>
                <Button size="sm" onClick={openAdd}>
                    <Plus className="w-4 h-4 mr-1" /> Add Field
                </Button>
            </div>

            {/* Fields table */}
            {loading ? (
                <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 text-stone-400 animate-spin" /></div>
            ) : fields.length === 0 ? (
                <div className="text-center py-10 text-stone-400">
                    <p className="text-sm">No custom fields for {entity}s yet.</p>
                    <p className="text-xs mt-1">Click "Add Field" to create one.</p>
                </div>
            ) : (
                <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
                    <table className="w-full text-sm">
                        <thead className="bg-stone-50 border-b border-stone-200">
                            <tr>
                                <th className="text-left px-4 py-2.5 text-xs font-semibold text-stone-500 uppercase w-8">#</th>
                                <th className="text-left px-4 py-2.5 text-xs font-semibold text-stone-500 uppercase">Label</th>
                                <th className="text-left px-4 py-2.5 text-xs font-semibold text-stone-500 uppercase">Key</th>
                                <th className="text-left px-4 py-2.5 text-xs font-semibold text-stone-500 uppercase">Type</th>
                                <th className="text-center px-4 py-2.5 text-xs font-semibold text-stone-500 uppercase">Required</th>
                                <th className="text-right px-4 py-2.5 text-xs font-semibold text-stone-500 uppercase">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {fields.map((f, i) => (
                                <tr key={f.field_id} className="border-b border-stone-100 hover:bg-stone-50">
                                    <td className="px-4 py-2.5 text-stone-400">{i + 1}</td>
                                    <td className="px-4 py-2.5 font-medium text-stone-900">{f.label}</td>
                                    <td className="px-4 py-2.5 text-stone-400 font-mono text-xs">{f.key}</td>
                                    <td className="px-4 py-2.5">
                                        <span className="text-xs px-2 py-0.5 rounded-full bg-stone-100 text-stone-600 capitalize">
                                            {FIELD_TYPES.find(t => t.value === f.field_type)?.label || f.field_type}
                                        </span>
                                    </td>
                                    <td className="px-4 py-2.5 text-center">
                                        {f.required ? <span className="text-emerald-600 text-xs font-medium">Yes</span> : <span className="text-stone-300">—</span>}
                                    </td>
                                    <td className="px-4 py-2.5 text-right">
                                        <div className="flex items-center justify-end gap-1">
                                            <button onClick={() => openEdit(f)} className="p-1.5 text-stone-400 hover:text-stone-700"><Edit3 className="w-4 h-4" /></button>
                                            <button onClick={() => handleDelete(f.field_id)} className="p-1.5 text-stone-400 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Merge variable info */}
            {fields.length > 0 && (
                <div className="bg-stone-50 rounded-lg p-3 text-xs text-stone-500">
                    <p className="font-medium text-stone-600 mb-1">Email Template Merge Variables</p>
                    <div className="flex flex-wrap gap-2">
                        {fields.map(f => (
                            <code key={f.key} className="px-1.5 py-0.5 rounded bg-white border border-stone-200 text-stone-700 font-mono">
                                {`{{${f.label.replace(/\s/g, '')}}}`}
                            </code>
                        ))}
                    </div>
                </div>
            )}

            {/* Add/Edit Dialog */}
            <Dialog open={showDialog} onOpenChange={setShowDialog}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle>{editingField ? 'Edit' : 'Add'} Custom Field</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div>
                            <label className="block text-xs font-medium text-stone-600 mb-1">Label</label>
                            <input
                                value={form.label}
                                onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
                                placeholder="e.g. Annual Contract Value"
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                            />
                            {form.label && (
                                <p className="text-xs text-stone-400 mt-1">Key: <code className="font-mono">{labelToKey(form.label)}</code></p>
                            )}
                        </div>

                        <div>
                            <label className="block text-xs font-medium text-stone-600 mb-1">Field Type</label>
                            <select
                                value={form.field_type}
                                onChange={e => setForm(f => ({ ...f, field_type: e.target.value }))}
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                            >
                                {FIELD_TYPES.map(t => (
                                    <option key={t.value} value={t.value}>{t.label}</option>
                                ))}
                            </select>
                        </div>

                        <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <input
                                type="checkbox"
                                checked={form.required}
                                onChange={e => setForm(f => ({ ...f, required: e.target.checked }))}
                                className="rounded border-stone-300"
                            />
                            Required field
                        </label>

                        {showOptions && (
                            <div>
                                <label className="block text-xs font-medium text-stone-600 mb-1">Options</label>
                                <div className="flex gap-2 mb-2">
                                    <input
                                        value={optionInput}
                                        onChange={e => setOptionInput(e.target.value)}
                                        onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addOption())}
                                        placeholder="Type option and press Enter"
                                        className="flex-1 px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                                    />
                                    <Button variant="outline" size="sm" onClick={addOption}>Add</Button>
                                </div>
                                <div className="flex flex-wrap gap-1.5">
                                    {form.options.map(opt => (
                                        <span key={opt} className="inline-flex items-center gap-1 px-2 py-1 bg-stone-100 rounded-lg text-xs text-stone-700">
                                            {opt}
                                            <button onClick={() => removeOption(opt)} className="text-stone-400 hover:text-red-500">&times;</button>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div>
                            <label className="block text-xs font-medium text-stone-600 mb-1">Default Value (optional)</label>
                            <input
                                value={form.default_value || ''}
                                onChange={e => setForm(f => ({ ...f, default_value: e.target.value }))}
                                className="w-full px-3 py-2 text-sm border border-stone-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setShowDialog(false)}>Cancel</Button>
                        <Button onClick={handleSave} disabled={saving}>
                            {saving ? 'Saving...' : editingField ? 'Update' : 'Create'} Field
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
